import { useCallback, useEffect, useMemo, useState } from 'react'

import { getSignedUrl, getSignedUrls } from '@/api/api'
import { resolveDirectUrl } from '@/api/client'

/**
 * 署名付きURLのキャッシュ（サーバー側 expires クエリに連動してキャッシュ）
 */
const signedUrlCache = new Map<string, { url: string; expiresAt: number }>()

/**
 * expires をミリ秒で表現したときに、その少し前にキャッシュを切るための安全マージン
 * クロックずれや HTTP リクエスト中の期限切れを避けるため
 */
const SAFETY_MARGIN_MS = 60 * 1000

/**
 * expires クエリが取得できなかった場合のフォールバック TTL（50分）
 */
const FALLBACK_TTL_MS = 50 * 60 * 1000

/**
 * 署名付きURLの `expires` クエリ（UNIX 秒）から、ミリ秒の有効期限を抽出する
 */
const parseExpiresFromUrl = (url: string): number | null => {
  try {
    const parsed = new URL(url, window.location.origin)
    const expires = parsed.searchParams.get('expires')
    if (!expires) return null
    const expiresSec = parseInt(expires, 10)
    if (!Number.isFinite(expiresSec) || expiresSec <= 0) return null
    return expiresSec * 1000
  } catch {
    return null
  }
}

/**
 * キャッシュから署名付きURLを取得（有効期限内の場合）
 */
const getCachedUrl = (path: string): string | null => {
  const cached = signedUrlCache.get(path)
  if (cached && Date.now() < cached.expiresAt) {
    return cached.url
  }
  signedUrlCache.delete(path)
  return null
}

/**
 * 署名付きURLをキャッシュに保存（サーバー側 expires と連動）
 */
const setCachedUrl = (path: string, url: string): void => {
  const expiresMs = parseExpiresFromUrl(url)
  const expiresAt = expiresMs ? expiresMs - SAFETY_MARGIN_MS : Date.now() + FALLBACK_TTL_MS
  signedUrlCache.set(path, { url, expiresAt })
}

/**
 * キャッシュを無効化（エラー時の再取得用）
 */
const invalidateCache = (path: string): void => {
  signedUrlCache.delete(path)
}

/**
 * 単一の署名付きURLを取得するフック
 *
 * 署名付きURLを取得し、Viteプロキシをバイパスしてバックエンドに直接アクセスできる
 * 絶対URLに変換して返す（本番環境対応）。
 *
 * @param path メディアのパス（例: /products/monsnode/123/thumbnail）
 * @returns { url: string | null, isLoading: boolean, error: Error | null, refetch: () => void }
 */
export const useSignedUrl = (path: string | null) => {
  const [url, setUrl] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const [retryCount, setRetryCount] = useState(0)

  const fetchSignedUrl = useCallback(async (targetPath: string, skipCache = false) => {
    // キャッシュチェック（skipCacheがfalseの場合のみ）
    if (!skipCache) {
      const cached = getCachedUrl(targetPath)
      if (cached) {
        setUrl(resolveDirectUrl(cached))
        return
      }
    } else {
      // キャッシュを無効化
      invalidateCache(targetPath)
    }

    setIsLoading(true)
    setError(null)

    try {
      const signedUrl = await getSignedUrl(targetPath)
      if (!signedUrl) {
        throw new Error('署名付きURLが空です')
      }
      setCachedUrl(targetPath, signedUrl)
      setUrl(resolveDirectUrl(signedUrl))
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '署名付きURL取得に失敗しました'
      // 認証エラーの場合は明確なメッセージを設定
      if (errorMessage.includes('認証')) {
        setError(new Error('認証が必要です。ログインしてください。'))
      } else {
        setError(err instanceof Error ? err : new Error(errorMessage))
      }
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!path) {
      setUrl(null)
      return
    }

    fetchSignedUrl(path, retryCount > 0)
  }, [path, retryCount, fetchSignedUrl])

  /**
   * キャッシュを無効化して再取得（画像読み込みエラー時に使用）
   */
  const refetch = useCallback(() => {
    setRetryCount((c) => c + 1)
  }, [])

  return { url, isLoading, error, refetch }
}

/**
 * 複数の署名付きURLをバッチ取得するフック
 *
 * @param paths メディアパスの配列
 * @returns { urls: Record<string, string>, isLoading: boolean, error: Error | null }
 */
export const useSignedUrls = (paths: string[]) => {
  const [urls, setUrls] = useState<Record<string, string>>({})
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  // pathsの内容を文字列化して依存配列で安定させる
  const pathsKey = useMemo(() => JSON.stringify(paths), [paths])

  useEffect(() => {
    if (paths.length === 0) {
      setUrls({})
      return
    }

    // キャッシュから取得できるものとできないものを分離
    const result: Record<string, string> = {}
    const uncachedPaths: string[] = []

    for (const path of paths) {
      const cached = getCachedUrl(path)
      if (cached) {
        result[path] = resolveDirectUrl(cached)
      } else {
        uncachedPaths.push(path)
      }
    }

    // すべてキャッシュヒットした場合
    if (uncachedPaths.length === 0) {
      setUrls(result)
      return
    }

    let isMounted = true
    setIsLoading(true)
    setError(null)

    getSignedUrls(uncachedPaths)
      .then((signedUrls) => {
        if (isMounted) {
          // キャッシュに保存
          for (const [path, url] of Object.entries(signedUrls)) {
            setCachedUrl(path, url)
            result[path] = resolveDirectUrl(url)
          }
          setUrls(result)
        }
      })
      .catch((err) => {
        if (isMounted) {
          setError(err instanceof Error ? err : new Error('署名付きURL取得に失敗しました'))
          // エラー時でもキャッシュから取得できたものは返す
          setUrls(result)
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false)
        }
      })

    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathsKey]) // pathsの内容が変わった時のみ再取得

  return { urls, isLoading, error }
}

/**
 * 署名付きURLキャッシュをクリア（ログアウト時などに使用）
 */
export const clearSignedUrlCache = (): void => {
  signedUrlCache.clear()
}
